'use client';

// A showcase room, rendered by the editor's own 3D viewport.
//
// Not a screenshot of one: this is `SceneCanvas`, the same component the
// editor draws with, seeded with the template's real `EditorDoc` through a
// read-only `EditorProvider`. Before/After is its `hidePanels` prop — the
// exact switch the result step's own compare toggle uses (SceneHost.tsx) —
// so "before" is the room with the treatment taken out rather than a second
// asset that has to be kept in step with the first.
//
// It also paints the room's FIELD — pressure, reflections or air velocity,
// whichever `showcaseViz.ts` assigns it — through the editor's own
// `useModalViz`. That matters structurally, not just for looks: this component
// is what BOTH the homepage's listening room (homepage/listen/ListeningRoom.tsx)
// and the in-product pre-upsell (PreUpsell.tsx) render, so there is exactly
// one implementation of "how a showcase room draws its field" and the two
// surfaces cannot drift into showing the same room differently.
//
// Consequences worth knowing:
//
//   * `staticView`. The editor's CameraRig and OrbitControls keep per-canvas
//     camera state — correct for a viewport a user orbits, fatal for two
//     canvases that must agree. With both out, the camera is exactly
//     `cameraPosition` looking at the room centre in both, so a wipe reads as
//     one room changing rather than as two photographs that nearly line up.
//   * `pointer-events-none`. SceneCanvas always mounts OrbitControls, and an
//     orbit on one canvas would desynchronise it from its twin. The wipe owns
//     the pointer; the render is a picture that happens to be live.
//   * Loaded through `next/dynamic` with `ssr: false`. Three.js has no
//     business in the server bundle of the dashboard and landing pages, where
//     this dialog's provider also mounts, and R3F is client-only regardless.

import dynamic from 'next/dynamic';
import { memo, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { EditorProvider, useDispatch } from '@/components/room/state/EditorContext';
import { VizSnapshotProvider } from '@/components/room/state/VizSnapshotContext';
import { useModalViz } from '@/components/room/hooks/useModalViz';
import { distanceToFitSphere, roomBoundingRadius } from '@/components/room/scene/cameraFraming';
import type { ShowcaseRoom } from './rooms';
import type { PersistedVizPayload } from '@roomtreatment/core';
import { usePrefersReducedMotion } from '@/lib/usePrefersReducedMotion';
import { showcaseVizSpec, useShowcaseViz } from './showcaseViz';

const SceneCanvas = dynamic(() => import('@/components/room/scene/SceneCanvas'), {
  ssr: false,
});

/** SceneHost's viewing ANGLE, verbatim — a pre-upsell that looked at its room
 *  from somewhere else than the editor the user just left would undo the
 *  continuity this screen is built on. Only the distance is recomputed, and
 *  it has to be: `staticView` drops CameraRig, which is what normally fits
 *  the room to the canvas, and SceneHost's literal distance was chosen for a
 *  tall viewport — in a 16:9 band it leaves a 5 × 4 m studio floating in
 *  space. The fit is the rig's own formula (cameraFraming.ts) at the frame's
 *  known aspect, so a vaulted 8 × 6 theatre and a small studio both fill it. */
const ANGLE: [number, number, number] = [11.5, 7.5, 11];
const FOV_RAD = (45 * Math.PI) / 180;
/** Fraction of the smaller axis the room's BOUNDING SPHERE occupies. Higher
 *  than CameraRig's 0.65: the rig frames a viewport a user works in and wants
 *  headroom to pan into, and this is a showcase that should be as close as it
 *  can get without clipping a corner.
 *
 *  Worth knowing before tuning it: the sphere is circumscribed, so the room
 *  itself never fills as much of the frame as this number suggests — a 7 x 5 m
 *  room at 0.82 covers about two thirds of the height. Callers with a frame
 *  taller than a dialog's pass their own (`fill`). */
const FILL = 0.82;
/** Until the box is measured. Any value draws a frame that the first
 *  ResizeObserver callback corrects a tick later; this one is a sane
 *  landscape so the correction is small rather than a jump. */
const ASPECT_FALLBACK = 3 / 2;

/** SceneHost's angle as spherical coordinates about the room centre — the
 *  starting point an orbit drags away from. */
export const DEFAULT_ORBIT: Orbit = {
  az: Math.atan2(ANGLE[0], ANGLE[2]),
  el: Math.asin(ANGLE[1] / Math.hypot(...ANGLE)),
};

export interface Orbit { az: number; el: number }

/**
 * Marketing-only overrides. All optional and all off by default, so the
 * landing showcase and the pre-upsell keep the behaviour documented above:
 * the field belongs to the untreated half, in the design system's own ink,
 * on this canvas's own clock.
 *
 * They exist for /design/loop, the page the product's looping GIFs are
 * recorded from, where the two halves have to show the SAME sweep at the
 * same moment and the treated one recolours rather than empties.
 */
export interface MarketingVizOpts {
  /** Paint the field on the treated half too, instead of clearing it. */
  vizOnBoth?: boolean;
  /** Recolours the velocity cloud (hex). */
  velocityInk?: string;
  /** Shared sweep clock in `performance.now()` seconds — see SceneCanvas. */
  sweepEpoch?: number;
  /** Device-pixel-ratio clamp. Defaults to the showcase's own thrifty pair. */
  dpr?: [number, number];
  /** Drive frames by hand: forces `frameloop='never'`, so nothing renders
   *  until the caller invokes r3f's `advance(t)` — which sets the clock to
   *  exactly t on every mounted root. The capture pipeline's whole
   *  determinism rests on this one prop. */
  manualFrames?: boolean;
}

/**
 * Memoised, and that is load-bearing rather than a micro-optimisation.
 *
 * `RoomWipe` animates the divider by calling `setPct` on every animation
 * frame, so it re-renders about 60 times a second for the life of the page.
 * None of this component's props change across those renders — but without
 * `memo` React still walks both rooms' whole subtrees each time, which is a
 * second full-rate workload sitting on top of the WebGL one and a large part
 * of why the marketing page felt heavy to scroll.
 */
const UpsellRoom = memo(function UpsellRoom({
  room, panels, orbit = DEFAULT_ORBIT, fill = FILL, className = '',
  vizOnBoth = false, velocityInk, sweepEpoch, dpr, manualFrames = false,
}: {
  room: ShowcaseRoom;
  /** True renders the treated room, false the bare one. */
  panels: boolean;
  /** How much of the frame the room's bounding sphere fills. See FILL — a
   *  bigger frame wants a bigger number, or the room floats in it. */
  fill?: number;
  /** Where the camera sits, in radians about the room centre. Passing the
   *  SAME value to two of these is what keeps a wipe's halves registered:
   *  the camera is a prop, not something each canvas discovers for itself. */
  orbit?: Orbit;
  className?: string;
} & MarketingVizOpts) {
  // Measured, not assumed. The frame is portrait on a phone and landscape on
  // a desktop, and the fit depends on which axis binds — a hardcoded aspect
  // frames one of the two wrong, which is how the room came out postage-stamp
  // sized on a narrow screen.
  //
  // A layout effect, and it reads the box once up front: a ResizeObserver
  // alone reports after the first paint, so a portrait frame was drawn once
  // at the fallback aspect and then visibly re-zoomed.
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [aspect, setAspect] = useState(ASPECT_FALLBACK);
  useLayoutEffect(() => {
    if (!node) return;
    const apply = (width: number, height: number) => {
      if (width > 0 && height > 0) setAspect(width / height);
    };
    apply(node.clientWidth, node.clientHeight);
    const ro = new ResizeObserver(([e]) => apply(e.contentRect.width, e.contentRect.height));
    ro.observe(node);
    return () => ro.disconnect();
  }, [node]);

  // Stop drawing once the room leaves the viewport.
  //
  // The scene runs several unconditional per-frame loops (SceneCanvas's
  // `frameloop` prop documents them), the wipe mounts TWO of these, and the
  // landing page keeps them alive for the rest of the session once its
  // showcase section has been revealed. That is the "everything feels heavy to
  // scroll" cost, and it is paid entirely for pixels nobody is looking at.
  //
  // Starts true so the first paint happens even if the observer has not
  // reported yet — a canvas that mounts already on screen must not wait a tick
  // to draw. `rootMargin` keeps a screenful of warm-up either side so the room
  // is never caught mid-resume as it scrolls into view.
  const [onScreen, setOnScreen] = useState(true);
  useEffect(() => {
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      ([e]) => setOnScreen(e.isIntersecting),
      { rootMargin: '100% 0px' },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [node]);

  // The field sweep is decorative motion, so it honours prefers-reduced-motion
  // — the same setting the divider's own animation already respects.
  const reducedMotion = usePrefersReducedMotion();

  const vizState = useShowcaseViz(room.id);

  const camera = useMemo<[number, number, number]>(() => {
    const { Lx, Ly, H } = room.doc.dims;
    const dist = distanceToFitSphere(roomBoundingRadius(Lx, Ly, H), FOV_RAD, aspect, fill);
    // About the room's centre, not the world origin: the fit is a distance
    // FROM the room, and a room whose corner sits at the origin would
    // otherwise drift out of frame as it got larger.
    const cx = Math.cos(orbit.el) * dist;
    return [
      Lx / 2 + cx * Math.sin(orbit.az),
      H / 2 + Math.sin(orbit.el) * dist,
      Ly / 2 + cx * Math.cos(orbit.az),
    ];
  }, [room.doc.dims, orbit, aspect, fill]);

  return (
    <div
      ref={setNode}
      className={`pointer-events-none absolute inset-0 ${className}`}
      data-testid="upsell-room"
    >
      <EditorProvider initialDoc={room.doc} readOnly>
        <VizSnapshotProvider>
          <RoomScene
            roomId={room.id}
            camera={camera}
            panels={panels}
            onScreen={onScreen}
            persistedViz={vizState.viz}
            sweep={!reducedMotion}
            vizOnBoth={vizOnBoth}
            velocityInk={velocityInk}
            sweepEpoch={sweepEpoch}
            dpr={dpr}
            manualFrames={manualFrames}
          />
        </VizSnapshotProvider>
      </EditorProvider>
    </div>
  );
});

export default UpsellRoom;

/**
 * The canvas, inside the provider so it can drive the editor's own viz state.
 *
 * Split out for one reason: `useModalViz` and the `SET_VIZ` / `SET_COMPARE`
 * dispatches below only exist inside an `EditorProvider`, and `UpsellRoom` is
 * the component that creates it. Everything here is the editor's real
 * machinery — the same hook, the same actions, the same `SceneCanvas` props —
 * so the field on this room is produced the way the editor produces it rather
 * than by a second implementation that renders "close enough".
 */
function RoomScene({
  roomId, camera, panels, onScreen, persistedViz, sweep,
  vizOnBoth, velocityInk, sweepEpoch, dpr, manualFrames,
}: MarketingVizOpts & {
  roomId: string;
  camera: [number, number, number];
  panels: boolean;
  onScreen: boolean;
  /** Already resolved by the parent — see the mount gate in `UpsellRoom`. */
  persistedViz: PersistedVizPayload | null;
  /** Scrub the field along its own axis; false holds it still. */
  sweep: boolean;
}) {
  const dispatch = useDispatch();
  const spec = showcaseVizSpec(roomId);

  // The two halves of the wipe are two different KINDS of picture, not the
  // same picture with more objects in it:
  //
  //   panels on  — the room as designed. Real colours, real floor, furniture.
  //   panels off — the room as the solver sees it: `renderMode: 'physics'`
  //                strips every fill to one white (renderMode.ts) and the
  //                field is the only thing left with colour in it.
  //
  // So dragging the divider does not reveal panels against an unchanged room;
  // it crosses from the problem to the design. The field belongs on the
  // untreated side, which is also why `compare` is pinned to `before`: that
  // slot IS the no-panel field.
  useEffect(() => {
    dispatch({ type: 'SET_COMPARE', compare: 'before' });
  }, [dispatch]);

  useEffect(() => {
    dispatch({ type: 'SET_RENDER_MODE', mode: panels ? 'design' : 'physics' });
    dispatch({
      type: 'SET_VIZ',
      mode: panels && !vizOnBoth ? 'none' : (spec?.mode ?? 'none'),
    });
    if (spec) dispatch({ type: 'SET_MODAL_FREQ', freq: spec.modalFreq });
  }, [dispatch, panels, vizOnBoth, spec?.mode, spec?.modalFreq]); // eslint-disable-line react-hooks/exhaustive-deps

  const [field, setField] = useState<Field>(NO_FIELD);
  const { modalResp, raytraceResp, volumeResp, velocityResp } = field;

  return (
    <>
      {/* Keyed on whether the field has arrived, which remounts the SOURCE
          exactly once — when it does — and never the canvas.
          `useModalViz` reads its persisted payload in a state initialiser
          (`useIdempotentState` is `useState` underneath), so a payload that
          lands after mount is never picked up and the field silently never
          appears. The key used to sit on this whole component, which tore the
          WebGL context down and built a new one: the room drew, blanked, and
          drew again — twice over in a wipe. Holding the room back until the
          bundle downloads is no better (the studio's cube is 5 MB). So the room
          stays up and the field joins it. */}
      <FieldSource
        key={persistedViz ? 'with-field' : 'no-field'}
        persistedViz={persistedViz}
        onField={setField}
      />
      <SceneCanvas
        modalResp={modalResp}
        raytraceResp={raytraceResp}
        volumeResp={volumeResp}
        velocityResp={velocityResp}
        // Where velocity RESTS when it is not sweeping: the slowest-air shell,
        // which is the reading a listening room is judged by. The sweep starts
        // and ends on this same value (VELOCITY_SWEEP_LEVEL), so the anchor is
        // the same either way.
        velocityLevel={0}
        // Only the untreated half carries a field, so only it sweeps.
        vizSweep={(vizOnBoth || !panels) && sweep}
        velocityInk={velocityInk}
        vizSweepEpoch={sweepEpoch}
        frameloop={manualFrames ? 'never' : onScreen ? 'always' : 'never'}
        // Two canvases per wipe, and this one is decoration rather than a
        // viewport being worked in — full retina here buys detail nobody
        // inspects at four times the fragment cost.
        dpr={dpr ?? [1, 1.5]}
        reflectionsRepresentation="points"
        // Skip the editor's 280ms particle ease: this canvas is often revealed
        // already in view, and a field that fades up after the room has landed
        // reads as a load rather than as part of the picture.
        volumeSeedIntensity={1}
        cameraPosition={camera}
        hidePanels={!panels}
        hideFurniture={!panels}
        interactive={false}
        // The screen's blue is a vertical gradient; an opaque clear colour
        // would cut a flat rectangle out of it exactly where the room is.
        transparent
        staticView
      />
    </>
  );
}

type Field = Pick<
  ReturnType<typeof useModalViz>, 'modalResp' | 'raytraceResp' | 'volumeResp' | 'velocityResp'
>;
const NO_FIELD: Field = { modalResp: null, raytraceResp: null, volumeResp: null, velocityResp: null };

/** Runs the editor's viz hook and reports what it resolves to. Renders
 *  nothing, so it can be remounted without touching the canvas — see the key
 *  on its mount. A layout effect, so a field that resolves in the same commit
 *  as its source reaches the canvas before that commit paints. */
function FieldSource({
  persistedViz, onField,
}: { persistedViz: PersistedVizPayload | null; onField: (field: Field) => void }) {
  const { modalResp, raytraceResp, volumeResp, velocityResp } = useModalViz('advanced', {
    readOnly: true,
    // The marketing showcase must never solve. Its bundles are trimmed to the
    // one layer each room paints, so read-only alone left every OTHER layer
    // looking absent and the page enqueued a real solve job per visitor.
    neverFetch: true,
    persistedViz,
  });
  useLayoutEffect(() => {
    onField({ modalResp, raytraceResp, volumeResp, velocityResp });
  }, [onField, modalResp, raytraceResp, volumeResp, velocityResp]);
  return null;
}
